import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from '@components/Common/index';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { HeadingRow, Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import AdminNavigation from '@components/Pages/Admin/AdminNavigation';
import UserEditor from './UserEditor';

// TanStack Query hooks
import {
  useUsersQuery,
  useUpdateUsersFromADMutation,
  User
} from '@api/queries/users';
import { useTerminalsQuery, Terminal } from '@api/queries/terminals';

// Re-export User type for other components
export type { User } from '@api/queries/users';

const UserList: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state for editor
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // TanStack Query for users and terminals
  const { data: users = [], isLoading: isLoadingUsers, refetch } = useUsersQuery();
  const { data: terminals = [], isLoading: isLoadingTerminals } = useTerminalsQuery();
  const updateFromADMutation = useUpdateUsersFromADMutation();

  const isLoading = isLoadingUsers || isLoadingTerminals;
  const isUpdatingFromAd = updateFromADMutation.isPending;
  const isRefreshing = isUpdatingFromAd || (isLoading && users.length > 0);

  // Create a map of terminals by ID for quick lookup
  const terminalsById = terminals.reduce((acc, terminal) => {
    acc[terminal.id] = terminal;
    return acc;
  }, {} as Record<string, Terminal>);

  const openEditor = (user: User) => {
    setSelectedUser(user);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedUser(null);
  };

  const formKey = (selectedUser && selectedUser.id) || 'new';

  return (
    <>
      <AdminNavigation isEnabled={true} isLoading={isRefreshing} fetch={() => refetch()} />
      <HeadingRow>
        <Button
          primary={true}
          disabled={isUpdatingFromAd}
          onClick={() => updateFromADMutation.mutate()}
        >
          {t('entry.update_users_from_ad')}
        </Button>
        {isUpdatingFromAd && <OperationLoader title={t('updating') + '...'} />}
      </HeadingRow>
      <Layout>
        <MainContent>
          <ContentContainer>
            {isLoading && !isRefreshing && <ContentLoader />}
            <Table
              data={users}
              columns={[
                {
                  Header: 'Id',
                  id: 'id',
                  accessor: 'id',
                  show: false
                },
                {
                  Header: t('users.name'),
                  id: 'name',
                  maxWidth: 420,
                  accessor: 'name'
                },
                {
                  Header: t('users.email'),
                  id: 'email',
                  accessor: 'email'
                },
                {
                  Header: t('users.terminals'),
                  id: 'terminals',
                  accessor: (user: User) =>
                    user.terminals
                      ? user.terminals.map(x => (terminalsById[x] ? terminalsById[x].name : 'n/a')).join(', ')
                      : ''
                }
              ]}
              defaultPageSize={20}
              onRowSelected={(row: any) => openEditor(row.original)}
              rowSelector={i => isEditorOpen && selectedUser && selectedUser.id === i.original.id}
            />
          </ContentContainer>
        </MainContent>
        {isEditorOpen && selectedUser && (
          <SideBar>
            <UserEditor
              key={formKey}
              selectedUser={selectedUser}
              terminals={terminals}
              onClose={closeEditor}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default UserList;
